import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle, ArrowUpRight, CheckCircle2, Clock, DollarSign, Filter,
  MessageCircle, Phone, Mail, Link2, Percent, RefreshCw, Search, ShoppingCart,
  Star, Target, TrendingDown, TrendingUp, Users, X, Zap, Eye, Tag, Send
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, resolveMediaUrl, PLACEHOLDER_IMAGE } from "@/api";

const money = (v = 0) => `₹${Number(v).toLocaleString("en-IN")}`;
const TABS = [
  { key: "all", label: "All" },
  { key: "high_value", label: "High Value" },
  { key: "hot_leads", label: "Hot Leads" },
  { key: "recovery_sent", label: "Recovery Sent" },
  { key: "recovered", label: "Recovered" },
  { key: "lost", label: "Lost" },
  { key: "today", label: "Today" },
  { key: "last_24h", label: "Last 24h" },
  { key: "last_7d", label: "Last 7d" },
];

const STATUS_CFG = {
  abandoned:      { label: "ABANDONED",      bg: "#1a0a0a", color: "#ef4444", border: "#ef444430" },
  recovery_sent:  { label: "RECOVERY SENT",  bg: "#0a1020", color: "#f59e0b", border: "#f59e0b30" },
  recovered:      { label: "RECOVERED",      bg: "#0a1a0a", color: "#22c55e", border: "#22c55e30" },
  lost:           { label: "LOST",           bg: "#12081a", color: "#a78bfa", border: "#a78bfa30" },
};

const SOURCE_CFG = {
  Website:   { color: "#00d4ff" }, WhatsApp: { color: "#22c55e" },
  Google:    { color: "#f59e0b" }, Facebook: { color: "#3b82f6" },
  Instagram: { color: "#a78bfa" }, YouTube:  { color: "#ef4444" },
  Direct:    { color: "#8fa5c4" },
};

const RECOVER_ACTIONS = [
  { key: "whatsapp",      Icon: MessageCircle, label: "WhatsApp",      color: "#22c55e" },
  { key: "sms",           Icon: Phone,         label: "SMS",           color: "#00d4ff" },
  { key: "email",         Icon: Mail,          label: "Email",         color: "#a78bfa" },
  { key: "recovery_link", Icon: Link2,         label: "Recovery Link", color: "#f59e0b" },
  { key: "discount",      Icon: Percent,       label: "Discount",      color: "#f59e0b" },
  { key: "call",          Icon: Phone,         label: "Call",          color: "#ef4444" },
];

// Mini sparkline bar chart
function Sparkline({ up = true }) {
  const bars = [30, 55, 40, 70, 50, 80, 65, 90].map(v => v + Math.random() * 10);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 28, marginTop: 4 }}>
      {bars.map((h, i) => (
        <div key={i} style={{ flex: 1, height: `${Math.min(100, h)}%`, background: up ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.4)", borderRadius: 2 }} />
      ))}
    </div>
  );
}

function StatCard({ label, value, change, up = true, icon: Icon, color, live }) {
  return (
    <div className="abc-stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="abc-stat-top">
        <div className="abc-stat-icon" style={{ background: `${color}15`, borderColor: `${color}25` }}>
          <Icon size={18} style={{ color }} />
        </div>
        {live && <span className="abc-live-dot"><span />LIVE</span>}
      </div>
      <div className="abc-stat-label">{label.toUpperCase()}</div>
      <div className="abc-stat-value">{value}</div>
      {change && (
        <div className="abc-stat-change" style={{ color: up ? "#22c55e" : "#ef4444" }}>
          {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {change}
        </div>
      )}
      <Sparkline up={up} />
    </div>
  );
}

function CustomerBadge({ type }) {
  const colors = { Premium: "#f59e0b", Regular: "#00d4ff", New: "#22c55e" };
  return <span style={{ fontSize: 10, fontWeight: 700, color: colors[type] || "#8fa5c4", background: `${colors[type] || "#8fa5c4"}15`, padding: "2px 6px", borderRadius: 4, marginLeft: 4 }}>{type}</span>;
}

function AbandonedCartRow({ cart, selected, onSelect, onView, isChecked, onCheck }) {
  const status = STATUS_CFG[cart.status] || STATUS_CFG.abandoned;
  const src = SOURCE_CFG[cart.source] || SOURCE_CFG.Website;
  return (
    <tr className={`abc-row${selected ? " selected" : ""}`} data-testid={`cart-row-${cart.id}`}>
      <td><input type="checkbox" checked={isChecked} onChange={e => onCheck(cart.id, e.target.checked)} /></td>
      <td><span className="abc-row-num">{cart.cart_id}</span></td>
      <td>
        <div className="abc-customer-cell">
          <div className="abc-avatar">{cart.customer_name?.[0]?.toUpperCase() || "?"}</div>
          <div>
            <b>{cart.customer_name}</b>
            <small>{cart.customer_phone || cart.customer_email}</small>
            <small>{cart.customer_city}</small>
          </div>
        </div>
      </td>
      <td>
        <b style={{ fontSize: 12 }}>{cart.cart_id}</b>
        <small style={{ display: "block", color: "#6b7f9e", fontSize: 10 }}>{cart.item_count} items</small>
      </td>
      <td>
        <div className="abc-product-thumbs">
          {cart.items?.slice(0, 3).map((item, i) => (
            <img key={i} src={resolveMediaUrl(item.image) || PLACEHOLDER_IMAGE} alt={item.name} className="abc-thumb" title={item.name} onError={e => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
          ))}
          {(cart.items?.length || 0) > 3 && <span className="abc-thumb-more">+{cart.items.length - 3}</span>}
        </div>
      </td>
      <td><b style={{ color: "#00d4ff", fontSize: 13 }}>{money(cart.cart_value)}</b></td>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Clock size={11} style={{ color: "#6b7f9e" }} />
          <span style={{ fontSize: 12, color: cart.ago_seconds < 3600 ? "#ef4444" : "#f59e0b" }}>{cart.ago_label}</span>
        </div>
      </td>
      <td>
        <span className="abc-status-pill" style={{ background: status.bg, color: status.color, borderColor: status.border }}>
          {status.label}
        </span>
      </td>
      <td><span style={{ fontSize: 11, color: src.color, fontWeight: 700 }}>{cart.source}</span></td>
      <td>
        <div style={{ display: "flex", gap: 4 }}>
          <button className="abc-action-icon" title="View" onClick={() => onView(cart)} data-testid={`view-cart-${cart.id}`}><Eye size={13} /></button>
          <button className="abc-action-icon green" title="WhatsApp" onClick={async () => { await api.post(`/api/admin/abandoned-carts/${cart.id}/recovery-action`, { action: "whatsapp" }); toast.success("WhatsApp action recorded"); }} data-testid={`whatsapp-${cart.id}`}><MessageCircle size={13} /></button>
          <button className="abc-action-icon" title="SMS" onClick={async () => { await api.post(`/api/admin/abandoned-carts/${cart.id}/recovery-action`, { action: "sms" }); toast.success("SMS action recorded"); }} data-testid={`sms-${cart.id}`}><Phone size={13} /></button>
          <button className="abc-action-icon purple" title="More" data-testid={`more-${cart.id}`}>•••</button>
        </div>
      </td>
    </tr>
  );
}

function CartDetailPanel({ cart, onClose, onActionSent }) {
  const [busy, setBusy] = useState("");
  const [noteTemplate, setNoteTemplate] = useState("Hi {name}, you left some amazing products in your cart at DMMobile Shop. Complete your order now.");
  const [leadBusy, setLeadBusy] = useState(false);

  const sendAction = async (action) => {
    setBusy(action);
    try {
      await api.post(`/api/admin/abandoned-carts/${cart.id}/recovery-action`, { action, note: noteTemplate.replace("{name}", cart.customer_name) });
      toast.success(`${action} action sent!`);
      onActionSent();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const markStatus = async (status) => {
    try {
      await api.post(`/api/admin/abandoned-carts/${cart.id}/mark-status?status=${status}`);
      toast.success(`Marked as ${status}`);
      onActionSent();
    } catch (e) { toast.error(apiError(e)); }
  };

  const generateLead = async () => {
    setLeadBusy(true);
    try {
      const { data } = await api.post(`/api/admin/abandoned-carts/${cart.id}/generate-lead`);
      if (data._already_exists) {
        toast.info("Active lead already exists for this customer");
      } else {
        toast.success(`Lead created for ${cart.customer_name}! Go to Leads section.`);
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setLeadBusy(false); }
  };

  return (
    <aside className="abc-detail-panel" data-testid="cart-detail-panel">
      <div className="abc-detail-head">
        <div>
          <span className="abc-detail-id">{cart.cart_id}</span>
          <span className="abc-status-pill" style={{ ...(STATUS_CFG[cart.status] || STATUS_CFG.abandoned), marginLeft: 8, fontSize: 10 }}>
            {(STATUS_CFG[cart.status] || STATUS_CFG.abandoned).label}
          </span>
          <small style={{ display: "block", color: "#6b7f9e", fontSize: 11, marginTop: 2 }}>Abandoned {cart.ago_label} · High recovery opportunity</small>
        </div>
        <button onClick={onClose} className="abc-close-btn" data-testid="close-cart-detail"><X size={16} /></button>
      </div>
      {/* Generate Lead CTA */}
      <button className="abc-generate-lead-btn" onClick={generateLead} disabled={leadBusy} data-testid="generate-lead-btn">
        <Target size={14} /> {leadBusy ? "Generating…" : "Generate Lead"}
      </button>

      {/* Customer */}
      <section className="abc-detail-section">
        <div className="abc-section-label"><Users size={13} /> Customer Details</div>
        <div className="abc-customer-detail">
          <div className="abc-avatar large">{cart.customer_name?.[0]?.toUpperCase() || "?"}</div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <b style={{ color: "#e2eafc" }}>{cart.customer_name}</b>
              <CustomerBadge type={cart.customer_type} />
            </div>
            <div className="abc-detail-row"><Phone size={11} /><span>{cart.customer_phone || "N/A"}</span></div>
            <div className="abc-detail-row"><Mail size={11} /><span>{cart.customer_email}</span></div>
            <div className="abc-detail-row"><span style={{ color: "#6b7f9e", fontSize: 11 }}>Delhi</span></div>
          </div>
        </div>
        <div className="abc-detail-stats">
          <div><span>Previous Orders</span><b>{cart.previous_orders}</b></div>
          <div><span>Total Lifetime Value</span><b>{money(cart.lifetime_value)}</b></div>
        </div>
      </section>

      {/* Cart items */}
      <section className="abc-detail-section">
        <div className="abc-section-label"><ShoppingCart size={13} /> Cart Details ({cart.item_count} Items)</div>
        <div className="abc-cart-meta">
          <div><span>Cart ID</span><b>{cart.cart_id}</b></div>
          <div><span>Cart Value</span><b>{money(cart.cart_value)}</b></div>
          <div><span>Abandoned</span><b>{cart.ago_label}</b></div>
          <div><span>Final Amount</span><b style={{ color: "#00d4ff" }}>{money(cart.cart_value)}</b></div>
        </div>
        <div className="abc-items-list" data-testid="cart-items-list">
          {cart.items?.map((item, i) => (
            <div key={i} className="abc-item-row">
              <img src={resolveMediaUrl(item.image) || PLACEHOLDER_IMAGE} alt={item.name} onError={e => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
              <div><b>{item.name}</b><small>Qty {item.quantity}</small></div>
              <span>{money(item.price)}</span>
              <span className="abc-in-stock" style={{ color: item.stock > 0 ? "#22c55e" : "#ef4444" }}>{item.stock > 0 ? "In Stock" : "Out"}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Activity timeline */}
      <section className="abc-detail-section">
        <div className="abc-section-label"><Clock size={13} /> Activity Timeline</div>
        <div className="abc-timeline">
          {[
            { text: "Customer entered website", color: "#00d4ff" },
            { text: "Viewed product page", color: "#00d4ff" },
            { text: "Added product to cart", color: "#00d4ff" },
            { text: "Viewed cart", color: "#00d4ff" },
            { text: "Started checkout", color: "#f59e0b" },
            { text: "Payment abandoned", color: "#ef4444" },
          ].map((ev, i) => (
            <div key={i} className="abc-tl-row">
              <div className="abc-tl-dot" style={{ background: ev.color }} />
              <span>{ev.text}</span>
            </div>
          ))}
          {cart.recovery_actions?.map((act, i) => (
            <div key={`r${i}`} className="abc-tl-row">
              <div className="abc-tl-dot" style={{ background: "#a78bfa" }} />
              <span style={{ color: "#a78bfa" }}>Recovery: {act.action} by {act.by}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Recovery center */}
      <section className="abc-detail-section" data-testid="recovery-center">
        <div className="abc-section-label"><Zap size={13} /> Recovery Center</div>
        <div className="abc-recover-grid">
          {RECOVER_ACTIONS.map(({ key, Icon, label, color }) => (
            <button key={key} className="abc-recover-btn" style={{ borderColor: `${color}25`, color }} onClick={() => sendAction(key)} disabled={busy === key} data-testid={`recover-${key}`}>
              {busy === key ? <RefreshCw size={13} className="spin-slow" /> : <Icon size={13} />} {label}
            </button>
          ))}
        </div>
        <label className="abc-template-label">Quick Template</label>
        <textarea className="abc-template-input" value={noteTemplate} onChange={e => setNoteTemplate(e.target.value)} rows={3} data-testid="recovery-template-input" />
        <button className="abc-send-btn" onClick={() => sendAction("whatsapp")} disabled={!!busy} data-testid="send-recovery-btn">
          <Send size={14} /> Send Message
        </button>
      </section>

      {/* Mark status */}
      <section className="abc-detail-section">
        <div style={{ display: "flex", gap: 8 }}>
          <button className="abc-mark-btn recovered" onClick={() => markStatus("recovered")} data-testid="mark-recovered"><CheckCircle2 size={13} /> Recovered</button>
          <button className="abc-mark-btn lost" onClick={() => markStatus("lost")} data-testid="mark-lost"><X size={13} /> Mark Lost</button>
        </div>
      </section>
    </aside>
  );
}

function HighValueCarousel({ carts }) {
  if (!carts?.length) return null;
  const hvCarts = carts.filter(c => c.cart_value >= 50000).slice(0, 6);
  if (!hvCarts.length) return null;
  return (
    <div className="abc-hv-section" data-testid="high-value-carts">
      <div className="abc-hv-head">
        <Star size={14} style={{ color: "#f59e0b" }} />
        <b>HIGH VALUE CARTS</b>
        <span>(Above ₹50,000)</span>
      </div>
      <div className="abc-hv-rail">
        {hvCarts.map(cart => (
          <div key={cart.id} className="abc-hv-card" data-testid={`hv-card-${cart.id}`}>
            <div className="abc-avatar sm">{cart.customer_name?.[0]?.toUpperCase()}</div>
            <div>
              <b>{cart.customer_name}</b>
              <small>{cart.items?.[0]?.name}</small>
              <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center" }}>
                <b style={{ color: "#00d4ff", fontSize: 13 }}>{money(cart.cart_value)}</b>
                <span className="abc-hv-dot" style={{ background: cart.ago_seconds < 3600 ? "#22c55e" : "#f59e0b" }} />
                <small style={{ color: "#6b7f9e" }}>{cart.ago_label}</small>
              </div>
            </div>
            <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
              <button className="abc-hv-btn whatsapp" onClick={async () => { await api.post(`/api/admin/abandoned-carts/${cart.id}/recovery-action`, { action: "whatsapp" }); toast.success("Sent!"); }}>
                <MessageCircle size={11} /> WhatsApp
              </button>
              <button className="abc-hv-btn recover" onClick={async () => { await api.post(`/api/admin/abandoned-carts/${cart.id}/mark-status?status=recovered`); toast.success("Marked recovered"); }}>
                Recover Cart
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminAbandonedCarts({ query = "" }) {
  const [carts, setCarts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedCart, setSelectedCart] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const pollRef = useRef();

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/api/admin/abandoned-carts/stats"); setStats(data); } catch (_) {}
  }, []);

  const loadCarts = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/admin/abandoned-carts?tab=${tab}&search=${encodeURIComponent(search)}`);
      setCarts(data || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [tab, search]);

  const refresh = useCallback(() => { loadStats(); loadCarts(); }, [loadStats, loadCarts]);

  useEffect(() => { refresh(); }, [refresh]);

  // Live polling every 15 seconds
  useEffect(() => {
    pollRef.current = setInterval(() => { loadStats(); loadCarts(); }, 15000);
    return () => clearInterval(pollRef.current);
  }, [loadStats, loadCarts]);

  const handleCheck = (id, val) => {
    setChecked(prev => { const n = new Set(prev); val ? n.add(id) : n.delete(id); return n; });
  };
  const allChecked = carts.length > 0 && carts.every(c => checked.has(c.id));
  const checkAll = (val) => setChecked(val ? new Set(carts.map(c => c.id)) : new Set());

  const exportCSV = () => {
    if (!carts.length) return toast.error("No cart data to export");
    const csvData = carts.map(c => ({
      "Cart ID": c.cart_id,
      "Customer Name": c.customer_name,
      "Phone": c.customer_phone,
      "Email": c.customer_email,
      "City": c.customer_city,
      "Cart Value (₹)": c.cart_value,
      "Items Count": c.item_count,
      "Products": c.items?.map(i => i.name).join(" + ") || "",
      "Status": c.status,
      "Customer Type": c.customer_type,
      "Cart Updated": new Date(c.abandoned_at).toLocaleString("en-IN"),
      "Previous Orders": c.previous_orders,
      "Lifetime Value (₹)": c.lifetime_value,
      "Source": c.source,
    }));
    const headers = Object.keys(csvData[0]);
    const rows = csvData.map(row => headers.map(h => `"${String(row[h] ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = "\ufeff" + [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `carts-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success(`Exported ${csvData.length} records`);
  };

  const visibleCarts = query ? carts.filter(c => [c.customer_name, c.customer_phone, c.cart_id].some(v => (v || "").toLowerCase().includes(query.toLowerCase()))) : carts;

  const S = stats || {};
  return (
    <div className="abc-root" data-testid="abandoned-cart-manager">
      {/* Header */}
      <div className="abc-page-head">
        <div className="abc-head-left">
          <ShoppingCart size={22} style={{ color: "#00d4ff" }} />
          <div>
            <h2>Abandoned Cart Control Center</h2>
            <p>Monitor, manage and recover every abandoned cart in real time.</p>
          </div>
        </div>
        <div className="abc-head-right">
          <div className="abc-live-synced"><span className="abc-live-dot"><span />LIVE DATA</span><small>Last synced: Just now</small></div>
          <button className="abc-refresh-btn" onClick={refresh} data-testid="refresh-abandoned-carts"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="abc-stats-grid">
        <StatCard label="Total Abandoned Carts"       value={S.total_abandoned || 0}                 change="+12.8% today"    up  icon={ShoppingCart}   color="#ef4444" live />
        <StatCard label="Cart Value at Risk"           value={money(S.cart_value_at_risk || 0)}       change="+8.4%"           up  icon={DollarSign}     color="#f59e0b" />
        <StatCard label="Today's Abandoned Carts"     value={S.today_abandoned || 0}                 change="+10.6%"          up  icon={AlertCircle}    color="#00d4ff" />
        <StatCard label="Recoverable Value"           value={money(S.recoverable_value || 0)}        change="+15.2%"          up  icon={TrendingUp}     color="#22c55e" />
        <StatCard label="Recovered Carts"             value={S.recovered_carts || 0}                 change="+18.7%"          up  icon={CheckCircle2}   color="#22c55e" />
        <StatCard label="Recovery Rate"               value={`${S.recovery_rate || 0}%`}             change="+4.2%"           up  icon={Percent}        color="#a78bfa" />
        <StatCard label="High Value Carts"            value={S.high_value_carts || 0}                change="+22.1%"          up  icon={Star}           color="#f59e0b" />
        <StatCard label="New Abandonments"            value={S.new_abandonments_last_hour || 0}      change="Last 60 minutes" up  icon={Zap}            color="#ef4444" />
      </div>

      <div className="abc-content-area">
        <div className="abc-main-panel">
          {/* Filter tabs */}
          <div className="abc-filter-row">
            <div className="abc-tabs">
              {TABS.map(t => (
                <button key={t.key} className={`abc-tab${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>{t.label}</button>
              ))}
            </div>
            <div className="abc-search-wrap">
              <Search size={13} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search carts..." data-testid="cart-search-input" />
            </div>
            <button className="abc-filter-btn" data-testid="cart-filter-btn"><Filter size={13} /> Filters</button>
          </div>

          {/* Table */}
          <div className="abc-table-wrap">
            <div className="abc-table-head-row">
              <div className="abc-table-title">
                <span className="abc-live-dot"><span />LIVE ABANDONED CARTS</span>
                <small>Real-time updates</small>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {checked.size > 0 && <span style={{ fontSize: 12, color: "#00d4ff", alignSelf: "center" }}>{checked.size} selected</span>}
                <button className="abc-bulk-btn" data-testid="bulk-actions-btn"><Zap size={13} /> Bulk Actions</button>
                <button className="abc-export-btn" onClick={exportCSV} data-testid="export-btn"><ArrowUpRight size={13} /> Export CSV</button>
              </div>
            </div>
            {loading ? (
              <div className="abc-loading"><RefreshCw size={18} className="spin-slow" /><span>Loading abandoned carts…</span></div>
            ) : visibleCarts.length === 0 ? (
              <div className="abc-empty"><ShoppingCart size={32} /><h3>No abandoned carts</h3><p>All customers completed checkout. Great job!</p></div>
            ) : (
              <div className="abc-table-scroll">
                <table className="abc-table" data-testid="abandoned-carts-table">
                  <thead>
                    <tr>
                      <th><input type="checkbox" checked={allChecked} onChange={e => checkAll(e.target.checked)} /></th>
                      <th>#</th><th>Customer</th><th>Cart Details</th><th>Products</th>
                      <th>Cart Value</th><th>Time Abandoned</th><th>Status</th><th>Source</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCarts.map((cart, i) => (
                      <AbandonedCartRow
                        key={cart.id}
                        cart={cart}
                        selected={selectedCart?.id === cart.id}
                        onSelect={setSelectedCart}
                        onView={setSelectedCart}
                        isChecked={checked.has(cart.id)}
                        onCheck={handleCheck}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* High Value Carts */}
          <HighValueCarousel carts={carts} />
        </div>

        {/* Detail Panel */}
        {selectedCart && (
          <CartDetailPanel
            cart={selectedCart}
            onClose={() => setSelectedCart(null)}
            onActionSent={() => { refresh(); api.get(`/api/admin/abandoned-carts/${selectedCart.id}`).then(({ data }) => setSelectedCart(data)).catch(() => {}); }}
          />
        )}
      </div>
    </div>
  );
}
